/**
 * Wires the payment typed client (payApi / `@/services/api/instances`) with
 * its baseURL, payment-specific auth headers, and error handlers. Mirrors
 * apiClientInitializer; the payment baseURL and headers differ (see legacy
 * `@/lib/apiHelpers` PaymentRequestCreator + updatePaymentAuthRequestData).
 */
import type { AxiosError } from 'axios';

import { CONFIGURABLE_PAY_API_ENDPOINT } from '@/lib/apiHelpers';
import { getAuthToken } from '@/services/api/auth';
import { getCompanyId } from '@/services/api/companyId';
import { getUsername, getAccountId } from '@/services/api/paymentAuth';
import { payApi } from '@/services/api/instances';
import { signOutAction, expiredAction } from '@/domain/env';

interface StoreLike {
  dispatch: (action: { type: string; payload?: unknown }) => void;
  getState: () => { env: { get: (key: 'locale') => string | undefined } };
}

const RELOAD_TRIES_KEY = 'dokka-app-reload-tries';
const MAX_RELOAD_TRIES = 4;

export default function paymentApiClientInitializer(store: StoreLike): void {
  payApi.configure({
    // The PAY service is no longer behind the AP `/v2/{client}/` prefix — it is
    // served from `/api/pay/` directly. Mirrored in @/domain/api.js
    // (PAY_API_ROOT_FULL), which the legacy payment client still uses.
    baseURL: `${CONFIGURABLE_PAY_API_ENDPOINT || ''}/pay/`,
    resolveAuthHeaders: async () => {
      // Read before the await so every header in the returned set describes the
      // same moment — the one the request was issued in.
      const companyId = getCompanyId();
      const token = await getAuthToken();
      const locale = store.getState().env.get('locale');
      const username = getUsername();
      const accountId = getAccountId();
      return {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(companyId && { CompanyId: companyId }),
        ...(locale && { 'Accept-Language': locale }),
        ...(username && { username }),
        ...(accountId && { 'X-Account-ID': accountId }),
      };
    },
    onAuthFailure: () => store.dispatch({ type: signOutAction.type }),
    onTrialExpired: () => store.dispatch({ type: expiredAction.type }),
    onAccessDenied: (err: AxiosError) => {
      const data = err.response?.data as { type?: string } | undefined;
      const accessDeniedReasonType = data?.type;
      store.dispatch({
        type: signOutAction.type,
        payload: {
          reason: process.env.REACT_APP_ACCESS_DENIED_CODE,
          ...(accessDeniedReasonType && { accessDeniedReasonType }),
        },
      });
    },
    onUpgradeRequired: () => {
      const tries = Number(window.localStorage.getItem(RELOAD_TRIES_KEY) ?? 0);
      if (tries < MAX_RELOAD_TRIES) {
        window.localStorage.setItem(RELOAD_TRIES_KEY, String(tries + 1));
        window.location.reload();
      } else {
        window.localStorage.removeItem(RELOAD_TRIES_KEY);
        store.dispatch({ type: signOutAction.type });
      }
    },
  });
}
